import React, { useState } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { Lock, Eye, EyeOff, Key, Save, AlertCircle, CheckCircle } from 'lucide-react';

const PasswordSection = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match.');
        return;
      }
      
      // Validate new password requirements
      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        setError(passwordErrors[0]); // Show first error
        return;
      }
      
      // Check if new password is different from current
      if (formData.currentPassword === formData.newPassword) {
        setError('New password must be different from your current password.');
        return;
      }
      
      // Update password using AWS Amplify
      await updatePassword({
        oldPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      
      // Reset form on success
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      setSuccess('Password changed successfully! You can now use your new password to sign in.');
    } catch (err: any) {
      console.error('Password change error:', err);
      
      // Handle specific AWS Cognito errors
      if (err.name === 'NotAuthorizedException') {
        setError('Current password is incorrect. Please try again.');
      } else if (err.name === 'InvalidPasswordException') {
        setError('New password does not meet the requirements. Please check the password requirements below.');
      } else if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to change password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear messages after 5 seconds
  React.useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Change Password</h2>
        <p className="text-neutral-600 dark:text-neutral-400">Update your password to keep your account secure.</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-green-800 dark:text-green-200">{success}</span>
        </div>
      )}
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Password */}
        <div>
          <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
            Current Password
          </label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              required
              className="w-full pl-10 pr-12 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white transition-colors"
              placeholder="Enter your current password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            >
              {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
            New Password
          </label>
          <div className="relative">
            <Key size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              required
              minLength={8}
              className="w-full pl-10 pr-12 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white transition-colors"
              placeholder="Enter your new password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            >
              {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="mt-1">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Password must meet all requirements listed below
            </p>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <Key size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="w-full pl-10 pr-12 py-3 border border-slate-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white transition-colors"
              placeholder="Confirm your new password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            >
              {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="p-4 bg-neutral-100 dark:bg-neutral-700 rounded-lg border border-slate-300 dark:border-neutral-600">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-2">Password Requirements:</h4>
          <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1">
            <li className={`flex items-center gap-2 ${
              formData.newPassword.length >= 8 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-neutral-600 dark:text-neutral-400'
            }`}>
              {formData.newPassword.length >= 8 ? '✓' : '•'} At least 8 characters long
            </li>
            <li className={`flex items-center gap-2 ${
              /[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword)
                ? 'text-green-600 dark:text-green-400' 
                : 'text-neutral-600 dark:text-neutral-400'
            }`}>
              {/[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword) ? '✓' : '•'} Contains uppercase and lowercase letters
            </li>
            <li className={`flex items-center gap-2 ${
              /\d/.test(formData.newPassword)
                ? 'text-green-600 dark:text-green-400' 
                : 'text-neutral-600 dark:text-neutral-400'
            }`}>
              {/\d/.test(formData.newPassword) ? '✓' : '•'} Contains at least one number
            </li>
            <li className={`flex items-center gap-2 ${
              /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword)
                ? 'text-green-600 dark:text-green-400' 
                : 'text-neutral-600 dark:text-neutral-400'
            }`}>
              {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword) ? '✓' : '•'} Contains at least one special character
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <div className="pt-6 border-t border-slate-300 dark:border-neutral-700">
          <button
            type="submit"
            disabled={
              isSubmitting || 
              !formData.currentPassword || 
              !formData.newPassword || 
              formData.newPassword !== formData.confirmPassword ||
              validatePassword(formData.newPassword).length > 0
            }
            className="flex items-center gap-2 px-6 py-3 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-tour="change-password-button"
          >
            <Save size={16} />
            {isSubmitting ? 'Changing Password...' : 'Change Password'}
          </button>
          
          {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              Passwords do not match
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default PasswordSection; 